import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, LICENSE_STATUS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, NativeSelect, Loading, Empty, LicenseStatusBadge } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LicenseRequestDialog } from "@/components/LicenseRequestDialog";
import { LicenseImportDialog } from "@/components/LicenseImportDialog";

export default function Licenses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [f, setF] = useState({ q: "", status: "", discipline_id: "", sector_id: "" });
  const [newOpen, setNewOpen] = useState(false);
  const [impOpen, setImpOpen] = useState(false);
  const reload = () => setF((cur) => ({ ...cur }));

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines(r.data));
    api.get("/sectors").then((r) => setSectors(r.data));
  }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/licenses", { params: f }).then((r) => setRows(r.data)).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [f]);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });

  return (
    <div>
      <PageHeader eyebrow="Registro nazionale" title="Licenze sportive" subtitle="Le licenze sono emesse e validate esclusivamente dalla sede nazionale MSP Italia. Gli stati scaduti si aggiornano automaticamente.">
        <Button variant="outline" onClick={() => setImpOpen(true)} data-testid="licenses-import-btn"><Upload className="mr-2 h-4 w-4" />Importa CSV/Excel</Button>
        <Button onClick={() => setNewOpen(true)} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="licenses-new-btn"><Plus className="mr-2 h-4 w-4" />{user.role === "admin" ? "Nuova licenza" : "Nuova richiesta"}</Button>
      </PageHeader>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={f.q} onChange={set("q")} placeholder="Cerca per atleta, numero licenza o tessera…" className="h-10 bg-white pl-9" data-testid="licenses-search-input" />
        </div>
        <NativeSelect value={f.status} onChange={set("status")} testid="licenses-status-filter">
          <option value="">Tutti gli stati</option>
          {Object.entries(LICENSE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </NativeSelect>
        <NativeSelect value={f.discipline_id} onChange={set("discipline_id")} testid="licenses-discipline-filter">
          <option value="">Tutte le discipline</option>
          {disciplines.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </NativeSelect>
        {user.role === "admin" && (
          <NativeSelect value={f.sector_id} onChange={set("sector_id")} testid="licenses-sector-filter">
            <option value="">Tutti i settori</option>
            {sectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </NativeSelect>
        )}
      </div>
      {rows === null ? <Loading /> : rows.length === 0 ? <Empty>Nessuna licenza trovata.</Empty> : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm" data-testid="licenses-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Numero</th><th className="px-4 py-3">Atleta</th><th className="px-4 py-3">Disciplina</th><th className="px-4 py-3">Categorie</th><th className="px-4 py-3">Settore richiedente</th><th className="px-4 py-3">Scadenza</th><th className="px-4 py-3">Stato</th></tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} onClick={() => navigate(`/licenze/${l.id}`)} className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-blue-50/40" data-testid={`license-row-${l.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{l.license_number || "—"}</td>
                  <td className="px-4 py-3"><div className="font-semibold text-slate-900">{l.athlete_name}</div><div className="font-mono text-xs text-slate-500">{l.membership_number}</div></td>
                  <td className="px-4 py-3">{l.discipline_name}</td>
                  <td className="px-4 py-3 text-slate-600">{l.categories.map((c) => c.name).join(", ")}</td>
                  <td className="px-4 py-3 text-slate-600">{l.sector_name}</td>
                  <td className="px-4 py-3">{fmtDate(l.expiry_date)}</td>
                  <td className="px-4 py-3"><LicenseStatusBadge status={l.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LicenseRequestDialog open={newOpen} onOpenChange={setNewOpen} onDone={(lic) => (lic ? navigate(`/licenze/${lic.id}`) : reload())} />
      <LicenseImportDialog open={impOpen} onOpenChange={setImpOpen} onDone={reload} />
    </div>
  );
}
